export interface CampaignLevel {
    worldId: number;
    levelId: number;
    seed: number;
    name: string;
    difficulty: number; // 1-10
    completed: boolean;
}

interface CampaignWorld {
    worldId: number;
    name: string;
    levels: CampaignLevel[];
}

export default class CampaignManager {
    private static readonly STORAGE_KEY = 'golfup-campaign-progress';
    private worlds: CampaignWorld[] = [];
    public currentWorld: number = 0;
    public currentLevel: number = 0;

    constructor() {
        this.initializeCampaign();
        this.loadProgress();
    }

    private initializeCampaign(): void {
        // Create 10 worlds with 10 levels each (100 total levels)
        this.worlds = [];
        
        for (let worldId = 0; worldId < 10; worldId++) {
            const world: CampaignWorld = {
                worldId,
                name: this.getWorldName(worldId),
                levels: []
            };

            for (let levelId = 0; levelId < 10; levelId++) {
                // Global level number (0-99)
                const globalLevel = worldId * 10 + levelId;
                // Deterministic seed: use global level number
                const seed = globalLevel;
                
                world.levels.push({
                    worldId,
                    levelId,
                    seed,
                    name: `Level ${globalLevel + 1}`,
                    difficulty: Math.min(10, Math.floor(globalLevel / 10) + 1),
                    completed: false
                });
            }

            this.worlds.push(world);
        }
    }
    
    /**
     * Calculate the height for a given level number
     * Base height is 5400, increases by 1% per level
     * Level 1 = 5400, Level 100 = ~14600
     */
    public getLevelHeight(levelNumber: number): number {
        const baseHeight = 5400;
        const growthRate = 0.01; // 1% per level
        return Math.floor(baseHeight * (1 + growthRate * levelNumber));
    }
    
    /**
     * Get global level number (0-99) from world and level IDs
     */
    public getGlobalLevelNumber(worldId?: number, levelId?: number): number {
        const w = worldId !== undefined ? worldId : this.currentWorld;
        const l = levelId !== undefined ? levelId : this.currentLevel;
        return w * 10 + l;
    }

    private getWorldName(worldId: number): string {
        const names = [
            'The Beginning',
            'Rising Heights',
            'Skyward Bound',
            'Cloud Kingdom',
            'The Summit',
            'Beyond the Clouds',
            'Stratospheric',
            'The Void',
            'Cosmic Heights',
            'The Final Ascent'
        ];
        return names[worldId] || `World ${worldId + 1}`;
    }
    
    /**
     * Get campaign settings including soft multiplayer preference
     */
    public getCampaignSettings(): { softMultiplayer: boolean } {
        const saved = localStorage.getItem('golfup-campaign-settings');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Failed to load campaign settings:', e);
            }
        }
        return { softMultiplayer: false };
    }
    
    /**
     * Save campaign settings
     */
    public saveCampaignSettings(settings: { softMultiplayer: boolean }): void {
        localStorage.setItem('golfup-campaign-settings', JSON.stringify(settings));
    }

    private loadProgress(): void {
        const saved = localStorage.getItem(CampaignManager.STORAGE_KEY);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                this.currentWorld = data.currentWorld || 0;
                this.currentLevel = data.currentLevel || 0;
                
                // Load completed levels
                if (data.completedLevels) {
                    data.completedLevels.forEach((completed: { worldId: number, levelId: number }) => {
                        const level = this.getLevel(completed.worldId, completed.levelId);
                        if (level) {
                            level.completed = true;
                        }
                    });
                }
            } catch (e) {
                console.error('Failed to load campaign progress:', e);
            }
        }
    }

    public saveProgress(): void {
        const completedLevels = this.worlds.flatMap(world => 
            world.levels
                .filter(level => level.completed)
                .map(level => ({ worldId: level.worldId, levelId: level.levelId }))
        );

        localStorage.setItem(CampaignManager.STORAGE_KEY, JSON.stringify({
            currentWorld: this.currentWorld,
            currentLevel: this.currentLevel,
            completedLevels
        }));
    }

    public getCurrentLevel(): CampaignLevel | null {
        return this.getLevel(this.currentWorld, this.currentLevel);
    }

    public getLevel(worldId: number, levelId: number): CampaignLevel | null {
        const world = this.worlds[worldId];
        if (!world) return null;
        return world.levels[levelId] || null;
    }

    public getCurrentWorld(): CampaignWorld | null {
        return this.worlds[this.currentWorld] || null;
    }

    public getAllWorlds(): CampaignWorld[] {
        return this.worlds;
    }

    public completeCurrentLevel(): void {
        const level = this.getCurrentLevel();
        if (level) {
            level.completed = true;
            this.saveProgress();
        }
    }

    public advanceToNextLevel(): boolean {
        const currentWorld = this.getCurrentWorld();
        if (!currentWorld) return false;

        // Move to next level in current world
        if (this.currentLevel < currentWorld.levels.length - 1) {
            this.currentLevel++;
            this.saveProgress();
            return true;
        }

        // Move to next world
        if (this.currentWorld < this.worlds.length - 1) {
            this.currentWorld++;
            this.currentLevel = 0;
            this.saveProgress();
            return true;
        }

        // Campaign complete!
        return false;
    }

    public getLevelSeed(worldId?: number, levelId?: number): number {
        const globalLevel = this.getGlobalLevelNumber(worldId, levelId);
        return globalLevel;
    }

    public isLevelUnlocked(worldId: number, levelId: number): boolean {
        // First level of first world is always unlocked
        if (worldId === 0 && levelId === 0) return true;

        // First level of a world is unlocked if previous world is complete
        if (levelId === 0) {
            const prevWorld = this.worlds[worldId - 1];
            if (!prevWorld) return false;
            return prevWorld.levels.every(level => level.completed);
        }

        // Other levels are unlocked if previous level in same world is completed
        const prevLevel = this.getLevel(worldId, levelId - 1);
        return prevLevel ? prevLevel.completed : false;
    }

    public getProgress(): { completed: number, total: number, percentage: number } {
        const total = this.worlds.reduce((sum, world) => sum + world.levels.length, 0);
        const completed = this.worlds.reduce((sum, world) => 
            sum + world.levels.filter(level => level.completed).length, 0
        );
        
        return {
            completed,
            total,
            percentage: total > 0 ? (completed / total) * 100 : 0
        };
    }

    public resetProgress(): void {
        this.currentWorld = 0;
        this.currentLevel = 0;
        this.worlds.forEach(world => {
            world.levels.forEach(level => {
                level.completed = false;
            });
        });
        this.saveProgress();
    }
}

